import { SoftSurveyComplete } from '@chiroup/components/survey/soft';
import { RequestStep } from '@chiroup/core/types/Request.type';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RequestBootstrap } from '../../services/request.service';
import StepBasicInformation from './StepBasicInformation';
import StepConsent from './StepConsent';
import StepInsurance from './StepInsurance';
import StepSurvey from './StepSurvey';
import StepUpload from './StepUpload';

const verbiage = {
  en: {
    of: 'of',
    step: 'Step',
    allDone: 'All set',
    allDoneBody: 'You’ve completed everything we needed before your visit. Thank you.',
    close: 'Close',
    unknownTitle: 'Unsupported step',
    unknownBody: 'This step type isn’t recognized.',
    skip: 'Skip',
    backToMenu: 'Back to menu',
  },
  es: {
    of: 'de',
    step: 'Paso',
    allDone: '¡Listo!',
    allDoneBody: 'Ha completado todo lo que necesitábamos antes de su visita. Gracias.',
    close: 'Cerrar',
    unknownTitle: 'Paso no compatible',
    unknownBody: 'Este tipo de paso no es reconocido.',
    skip: 'Saltar',
    backToMenu: 'Volver al menú',
  },
};

type Props = {
  bootstrap: RequestBootstrap;
  primaryColor?: string;
  kiosk?: boolean;
  // When true, the runner is being driven step-by-step from a parent
  // menu (currently just the kiosk). On completion of the lone step
  // we exit straight back to the parent instead of rendering the
  // runner's "All set" terminal panel — the parent decides what to
  // show next (the menu, with this step now marked complete).
  singleStepMode?: boolean;
  // Kiosk single-step mode only: `lastStep` marks this as the patient's
  // final outstanding step, and `onFinishAll` returns the kiosk to its
  // login screen. The survey completion panel uses these to redirect
  // straight to login (rather than back to the menu) once everything's
  // done.
  lastStep?: boolean;
  onFinishAll?: () => void;
  onUpdateRequest: (data: Partial<RequestBootstrap>) => void;
  onExit: () => void;
};

const RequestRunner: React.FC<Props> = ({
  bootstrap,
  primaryColor = '#77bd43',
  kiosk,
  singleStepMode,
  lastStep,
  onFinishAll,
  onUpdateRequest,
  onExit,
}) => {
  const language: 'en' | 'es' = bootstrap.language === 'es' ? 'es' : 'en';
  const v = verbiage[language];
  const validationCode = bootstrap.validationCode ?? '';

  // Sort steps and filter out anything kiosk should hide (legacy
  // behavior: kiosk skips Photo ID and Insurance card uploads).
  const orderedSteps = useMemo(() => {
    const sorted = [...bootstrap.steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (!kiosk) return sorted;

    return sorted.filter(s => s.type !== 'uploadPhotoId' && s.type !== 'uploadInsuranceCard');
  }, [bootstrap.steps, kiosk]);

  const firstIncomplete = orderedSteps.findIndex(s => !s.complete && !s.disabled);
  const [activeIndex, setActiveIndex] = useState<number>(
    firstIncomplete === -1 ? orderedSteps.length : firstIncomplete
  );

  const allDone = activeIndex >= orderedSteps.length;
  const activeStep: RequestStep | undefined = orderedSteps[activeIndex];

  // Single-step mode (kiosk picks one item from the menu). When the
  // step's own complete panel calls `completeStep`, we end up
  // `allDone` — fire `onExit` so control returns to the menu, where
  // the just-finished step now reads as Completed and the patient
  // can pick the next thing to work on. Skip the runner's own
  // "All set" terminal panel entirely; it'd be a misleading
  // "You've completed everything" when the patient may have only
  // done one of several items.
  useEffect(() => {
    if (allDone && singleStepMode) {
      onExit();
    }
  }, [allDone, singleStepMode, onExit]);

  // Refs so the deferred completeStep callback (fired after a
  // whole-survey submit) reads post-merge state instead of the stale
  // closure. Mirrors `stepsRef` in legacy Request.tsx:92.
  const orderedStepsRef = useRef(orderedSteps);
  const activeIndexRef = useRef(activeIndex);
  useEffect(() => {
    orderedStepsRef.current = orderedSteps;
  }, [orderedSteps]);
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const completeStep = useCallback(() => {
    const currentSteps = orderedStepsRef.current;
    const currentIndex = activeIndexRef.current;
    const currentActive = currentSteps[currentIndex];

    if (!currentActive) return;

    // Skip the FE-side complete write when the backend already marked
    // it; otherwise we race onUpdateRequest and can clobber queued
    // followups.
    const updatedSteps = currentActive.complete
      ? currentSteps
      : currentSteps.map((s, i) => (i === currentIndex ? { ...s, complete: true } : s));

    if (updatedSteps !== currentSteps) {
      onUpdateRequest({ steps: updatedSteps });
    }

    const nextIndex = updatedSteps.findIndex(
      (s, i) => i > currentIndex && !s.complete && !s.disabled
    );

    setActiveIndex(nextIndex === -1 ? updatedSteps.length : nextIndex);
  }, [onUpdateRequest]);

  // Bridges submitStuff's `(data, callback)` shape onto onUpdateRequest.
  // The setTimeout defer lets React flush the bootstrap update before
  // the callback runs (so completeStep sees fresh steps).
  const updateRequest = useCallback(
    (data: Partial<RequestBootstrap>, callback?: () => void) => {
      onUpdateRequest(data);
      if (callback) setTimeout(callback, 0);
    },
    [onUpdateRequest]
  );

  const visibleStepCount = orderedSteps.filter(s => !s.disabled).length;
  const completedStepCount = orderedSteps.filter(s => !s.disabled && s.complete).length;
  const currentDisplayIndex =
    orderedSteps.slice(0, activeIndex).filter(s => !s.disabled).length + 1;

  const renderStep = () => {
    if (!activeStep) return null;

    const stepTitle =
      language === 'es' && activeStep.esTitle ? activeStep.esTitle : activeStep.title;
    const stepDescription =
      language === 'es' && activeStep.esDescription
        ? activeStep.esDescription
        : activeStep.description;

    if (activeStep.type === 'survey' && activeStep.patientSurveyId && activeStep.survey) {
      return (
        <StepSurvey
          requestId={bootstrap.id}
          validationCode={validationCode}
          patientSurveyId={activeStep.patientSurveyId}
          survey={{
            id: activeStep.survey.id,
            title: stepTitle || activeStep.survey.title,
            subtitle: stepDescription,
            startMessage: activeStep.survey.startMessage,
            endMessage: activeStep.survey.endMessage,
            questions: activeStep.survey.questions,
            // Bootstrap-supplied resume data — the legacy
            // `getStepsToSend` strips `questions` for non-custom-
            // behavesAs surveys and instead pre-computes these so
            // the runner doesn't have to walk the entire question
            // list client-side.
            currentQuestion: activeStep.survey.currentQuestion,
            previousQuestion: activeStep.survey.previousQuestion,
            progress: activeStep.survey.progress,
            started: activeStep.survey.started,
            behavesAs: activeStep.survey.behavesAs,
            allowAdditionalComplaints: activeStep.survey.allowAdditionalComplaints,
          }}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          encounterId={bootstrap.encounterId}
          kiosk={kiosk}
          lastStep={lastStep}
          onFinishAll={onFinishAll}
          alreadyComplete={!!activeStep.complete}
          onComplete={completeStep}
          gender={bootstrap.gender}
          age={bootstrap.age}
          ccsFollowups={bootstrap.ccsFollowups}
          complaints={bootstrap.complaints}
          updateRequest={updateRequest}
        />
      );
    }

    if (activeStep.type === 'consent' && activeStep.patientConsentId) {
      return (
        <StepConsent
          requestId={bootstrap.id}
          validationCode={validationCode}
          patientConsentId={activeStep.patientConsentId}
          consent={{
            title: stepTitle || activeStep.consent?.title || '',
            url: activeStep.consent?.url || null,
          }}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          alreadyComplete={!!activeStep.complete}
          kiosk={kiosk}
          onComplete={completeStep}
        />
      );
    }

    if (activeStep.type === 'uploadPhotoId') {
      return (
        <StepUpload
          kind="photoId"
          requestId={bootstrap.id}
          validationCode={validationCode}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          alreadyComplete={!!activeStep.complete}
          onComplete={completeStep}
        />
      );
    }

    if (activeStep.type === 'uploadInsuranceCard') {
      return (
        <StepUpload
          kind="insuranceCard"
          requestId={bootstrap.id}
          validationCode={validationCode}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          alreadyComplete={!!activeStep.complete}
          onComplete={completeStep}
        />
      );
    }

    if (activeStep.type === 'insurance') {
      return (
        <StepInsurance
          requestId={bootstrap.id}
          validationCode={validationCode}
          language={language}
          primaryColor={primaryColor}
          alreadyComplete={!!activeStep.complete}
          kiosk={kiosk}
          prMap={bootstrap.prMap}
          clinicId={bootstrap.clinicId}
          step={activeStep}
          onComplete={completeStep}
        />
      );
    }

    if (activeStep.type === 'basicInformation') {
      return (
        <StepBasicInformation
          requestId={bootstrap.id}
          validationCode={validationCode}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          alreadyComplete={!!activeStep.complete}
          kiosk={kiosk}
          clinicId={bootstrap.clinicId}
          age={bootstrap.age}
          smsConsent={bootstrap.smsConsent}
          smsConsentMarketing={bootstrap.smsConsentMarketing}
          step={activeStep}
          onComplete={completeStep}
        />
      );
    }

    return (
      <div className="portal-card p-6">
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {v.unknownTitle}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{v.unknownBody}</p>
        <button
          type="button"
          onClick={completeStep}
          className="mt-4 inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold text-white"
          style={{ backgroundColor: primaryColor }}
        >
          {v.skip}
        </button>
      </div>
    );
  };

  const showInlineHeader = singleStepMode || (!allDone && visibleStepCount > 1);

  return (
    <div className="min-h-dvh flex flex-col antialiased">
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className={`mx-auto ${kiosk ? 'max-w-3xl' : 'max-w-2xl'} flex flex-col gap-4`}>
          {/* Lightweight inline header — replaces the old sticky
              top bar. Back-to-menu link on the left (single-step
              mode only — the sequential flow has no list to back
              out to), step indicator on the right. No logo, no
              progress bar; the page IS the form. */}
          {showInlineHeader && (
            <div className="flex items-center justify-between gap-3">
              {singleStepMode ? (
                <button
                  type="button"
                  onClick={onExit}
                  className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 -ml-1 px-1 py-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--ring]"
                  style={{ ['--ring' as any]: primaryColor } as React.CSSProperties}
                >
                  <ChevronLeftIcon className="size-4" />
                  {v.backToMenu}
                </button>
              ) : (
                <span />
              )}
              {!allDone && visibleStepCount > 1 && (
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 tabular-nums">
                  {v.step} {currentDisplayIndex} {v.of} {visibleStepCount}
                </p>
              )}
            </div>
          )}

          {allDone ? (
            singleStepMode ? null : (
              <SoftSurveyComplete
                language={language}
                primaryColor={primaryColor}
                title={v.allDone}
                message={v.allDoneBody}
                onExit={onExit}
                exitLabel={v.close}
              />
            )
          ) : (
            renderStep()
          )}
        </div>
      </main>
    </div>
  );
};

export default RequestRunner;
